import { useState } from 'react';
import { useLang } from '../contexts/LanguageContext';
import { ArrowLeft, Mail, Phone, MapPin, Send, Clock, Globe2, MessageSquare } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSending(true);
    setTimeout(() => {
      toast.success('Message sent! We will respond within 24 hours.');
      setForm({ name: '', email: '', subject: '', message: '' });
      setSending(false);
    }, 1000);
  };

  const inputCls = "bg-[#0B0F19] border-[#1E293B] text-[#F1F5F9] placeholder:text-[#475569]";

  return (
    <div className="min-h-screen bg-[#0B0F19]" data-testid="contact-page">
      <div className="px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto py-10">
        <Link to="/" className="flex items-center gap-1 text-sm text-[#94A3B8] hover:text-[#FADB5F] mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> {t('common.back')}
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#0A3A82]/20 flex items-center justify-center">
            <MessageSquare className="w-6 h-6 text-[#FADB5F]" />
          </div>
          <h1 className="text-3xl font-bold text-[#F1F5F9] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Contact Us
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6">
              <h2 className="text-base font-semibold text-[#F1F5F9] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Get in Touch</h2>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#0A3A82]/20 flex items-center justify-center shrink-0">
                    <Mail className="w-4 h-4 text-[#FADB5F]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#94A3B8]">Email</p>
                    <a href="mailto:Marketplacekoko@gmail.com" className="text-sm text-[#F1F5F9] hover:text-[#FADB5F] transition-colors">Marketplacekoko@gmail.com</a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#0A3A82]/20 flex items-center justify-center shrink-0">
                    <Phone className="w-4 h-4 text-[#FADB5F]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#94A3B8]">Phone</p>
                    <a href="tel:+35929001234" className="text-sm text-[#F1F5F9] hover:text-[#FADB5F] transition-colors">+359 2 900 1234</a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#0A3A82]/20 flex items-center justify-center shrink-0">
                    <MapPin className="w-4 h-4 text-[#FADB5F]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#94A3B8]">Address</p>
                    <p className="text-sm text-[#F1F5F9]">EuroMoto HQ<br />1000 Sofia, Bulgaria<br />European Union</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#0A3A82]/20 flex items-center justify-center shrink-0">
                    <Clock className="w-4 h-4 text-[#FADB5F]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#94A3B8]">Working Hours</p>
                    <p className="text-sm text-[#F1F5F9]">Mon - Fri: 09:00 - 18:00 CET<br />Sat: 10:00 - 14:00 CET</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#0A3A82]/20 flex items-center justify-center shrink-0">
                    <Globe2 className="w-4 h-4 text-[#FADB5F]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#94A3B8]">Service Area</p>
                    <p className="text-sm text-[#F1F5F9]">37+ European countries</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Departments */}
            <div className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6">
              <h3 className="text-sm font-semibold text-[#F1F5F9] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Departments</h3>
              <div className="space-y-2 text-xs text-[#94A3B8]">
                <p><strong className="text-[#F1F5F9]">General:</strong> <a href="mailto:Marketplacekoko@gmail.com" className="text-[#FADB5F] hover:underline">Marketplacekoko@gmail.com</a></p>
                <p><strong className="text-[#F1F5F9]">Support:</strong> <a href="mailto:Marketplacekoko@gmail.com" className="text-[#FADB5F] hover:underline">Marketplacekoko@gmail.com</a></p>
                <p><strong className="text-[#F1F5F9]">Payments:</strong> <a href="mailto:Marketplacekoko@gmail.com" className="text-[#FADB5F] hover:underline">Marketplacekoko@gmail.com</a></p>
                <p><strong className="text-[#F1F5F9]">Privacy:</strong> <a href="mailto:Marketplacekoko@gmail.com" className="text-[#FADB5F] hover:underline">Marketplacekoko@gmail.com</a></p>
                <p><strong className="text-[#F1F5F9]">Legal:</strong> <a href="mailto:Marketplacekoko@gmail.com" className="text-[#FADB5F] hover:underline">Marketplacekoko@gmail.com</a></p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6">
              <h2 className="text-base font-semibold text-[#F1F5F9] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Send us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">Name *</label>
                    <Input value={form.name} onChange={e => update('name', e.target.value)} placeholder="Your name" required className={inputCls} data-testid="contact-name" />
                  </div>
                  <div>
                    <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">Email *</label>
                    <Input type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder="your@email.com" required className={inputCls} data-testid="contact-email" />
                  </div>
                </div>
                <div>
                  <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">Subject</label>
                  <Input value={form.subject} onChange={e => update('subject', e.target.value)} placeholder="What is this about?" className={inputCls} data-testid="contact-subject" />
                </div>
                <div>
                  <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">Message *</label>
                  <Textarea value={form.message} onChange={e => update('message', e.target.value)} placeholder="How can we help you?" rows={6} required className={inputCls} data-testid="contact-message" />
                </div>
                <Button type="submit" disabled={sending} className="w-full bg-[#FADB5F] text-[#0F172A] hover:bg-[#E5C34A] font-semibold" data-testid="contact-submit-btn">
                  <Send className="w-4 h-4 mr-2" /> {sending ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
